import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { Button } from '@wordpress/components';
import { useEffect, useState } from '@wordpress/element';

const FIELD = {
	LEVEL: 0,
	DESIGNATION: 1,
	PERSON: 2,
	SUBSTITUTE: 3,
};

const createEmptyRow = () => ['', '', '', ''];

function normalizeRow(row, fallbackLevel) {
	// We store rows as arrays of length 4: [level, designation, personId, substituteId]
	if (Array.isArray(row)) {
		const r = [...row];
		while (r.length < 4) r.push('');
		r[FIELD.LEVEL] = (r[FIELD.LEVEL] ?? '') || fallbackLevel;
		return r.slice(0, 4).map((v) => (v == null ? '' : String(v)));
	}

	// Back-compat with older object shape {0:'',1:'',2:'',3:''}
	if (row && typeof row === 'object') {
		return [
			String(row[FIELD.LEVEL] ?? fallbackLevel ?? ''),
			String(row[FIELD.DESIGNATION] ?? ''),
			String(row[FIELD.PERSON] ?? ''),
			String(row[FIELD.SUBSTITUTE] ?? ''),
		];
	}

	const empty = createEmptyRow();
	empty[FIELD.LEVEL] = String(fallbackLevel ?? '');
	return empty;
}

function parseRankInformation(rankInfoDb) {
	// Best case: REST already returns an array (or array-like) of rows.
	if (Array.isArray(rankInfoDb)) {
		return rankInfoDb.length ? rankInfoDb.map((row, idx) => normalizeRow(row, String(idx + 1))) : [createEmptyRow()];
	}

	// Current plugin stores a serialized string; keep existing parsing approach but make it safe.
	if (typeof rankInfoDb === 'string' && rankInfoDb !== 'a:0:{}' && rankInfoDb !== 'a:1:{i:0;a:0:{}}') {
		const matches = rankInfoDb.match(/s:([0-9]+):"(.*?)";/g) || [];
		const flat = matches.map((s) => {
			const m = s.match(/s:([0-9]+):"(.*?)";/);
			return m ? m[2] : s;
		});

		const rows = [];
		for (let i = 0; i < flat.length; i += 4) {
			rows.push(normalizeRow(flat.slice(i, i + 4), String(rows.length + 1)));
		}
		return rows.length ? rows : [createEmptyRow()];
	}

	return [createEmptyRow()];
}

export const AddLabelForm = ({ attributes, sortedMembers, handleSubmit, closeModal }) => {
	const { categories } = attributes;
	const [rows, setRows] = useState([createEmptyRow()]);

	useEffect(() => {
		const fetchData = async () => {
			const catID = categories && categories.length > 0 ? categories[0].id : null;
			if (!catID) {
				setRows([createEmptyRow()]);
				return;
			}

			try {
				const taxonomy = await apiFetch({ path: `/wp/v2/group/${catID}` });
				const rankInfoDb = taxonomy ? taxonomy.rank_information : null;
				setRows(parseRankInformation(rankInfoDb));
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error fetching group rank_information:', error);
				setRows([createEmptyRow()]);
			}
		};

		fetchData();
	}, [categories]);


	const handleChange = (index, field, value) => {
		setRows((prev) => {
			const next = [...prev];
			const row = [...(next[index] || createEmptyRow())];
			row[field] = value;
			next[index] = row;
			return next;
		});
	};

	const handleAddRow = () => {
		setRows((prev) => [...prev, createEmptyRow()]);
	};

	const handleRemoveRow = (index) => {
		setRows((prev) => {
			if (prev.length > 1) {
				const next = [...prev];
				next.splice(index, 1);
				return next;
			}
			return [createEmptyRow()];
		});
	};


	const handleFormSubmit = async (e) => {
		e.preventDefault();
		if (!categories || categories.length === 0) {
			// No category selected; no-op.
			return;
		}

		// Format the data correctly (keep existing API format, but use our single rows state).
		// Each row: [level, designation, personId, substituteId]
		const formData = rows.map((row, index) => {
			const normalized = normalizeRow(row, String(index + 1));
			// If user edited the "level" field, respect it; otherwise default to index+1
			const level = normalized[FIELD.LEVEL] || String(index + 1);
			return [level, normalized[FIELD.DESIGNATION], normalized[FIELD.PERSON], normalized[FIELD.SUBSTITUTE]];
		});

		try {
			await saveTermMeta(formData);
			closeModal();
		} catch (error) {
			console.error('Error saving term meta:', error);
		}

		handleSubmit(formData);
		// console.log('Form Data:', formData);
	};


	const saveTermMeta = async (rankInformation) => {
		try {
			const termId = categories && categories.length > 0 ? categories[0].id : null;
			const response = await apiFetch({
				path: '/persons_info/v1/save_rank_information/',
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					term_id: termId,
					rank_information: rankInformation,
				}),
			});
			return response;
		} catch (error) {
			throw error;
		}
	};

	return (
		<>
			<form onSubmit={handleFormSubmit}>
				{rows.map((rowData, index) => {
					const row = normalizeRow(rowData, String(index + 1));
					return (
						<div key={index} className='artcloud-peoples-row'>
							<div id={`row-${index + 1}`} className='artcloud-people-member'>
								<input
									type="number"
									value={row[FIELD.LEVEL] || index + 1}
									onChange={(e) => handleChange(index, FIELD.LEVEL, e.target.value)}
								/>
								<input
									type="text"
									value={row[FIELD.DESIGNATION] || ''}
									onChange={(e) => handleChange(index, FIELD.DESIGNATION, e.target.value)}
									placeholder={__('Designation', 'artcloud-manage-people')}
								/>
								<select
									value={row[FIELD.PERSON] || ''}
									onChange={(e) => handleChange(index, FIELD.PERSON, e.target.value)}
								>
									<option value="">{__('Select People', 'artcloud-manage-people')}</option>
									{sortedMembers &&
										sortedMembers.map((post) => (
											<option key={post.id} value={post.id}>
												{post.title.rendered}
											</option>
										))}
								</select>
								<select
									value={row[FIELD.SUBSTITUTE] || ''}
									onChange={(e) => handleChange(index, FIELD.SUBSTITUTE, e.target.value)}
								>
									<option value="">{__('Select substitute', 'artcloud-manage-people')}</option>
									{sortedMembers &&
										sortedMembers
											.filter((post) => String(post.id) !== String(row[FIELD.PERSON] || ''))
											.map((post) => (
												<option key={post.id} value={post.id}>
													{post.title.rendered}
												</option>
											))}
								</select>
							</div>
							<a href="#" className="artcloud-people-delete" onClick={(e) => { e.preventDefault(); handleRemoveRow(index); }}>
								<span className="dashicons dashicons-trash"></span>
							</a>
						</div>
					);
				})}
				<Button variant="secondary" onClick={handleAddRow} className='artcloud-people-addMember'>
					{__('Add New', 'artcloud-manage-people')}
				</Button>
				<Button variant="primary" type="submit">
					{__('Save', 'artcloud-manage-people')}
				</Button>
			</form>
		</>
	);
};
